import { ReactNode, memo, forwardRef } from "react";

import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { Size } from "apps/website/components/layout/Spacer/Spacer.map";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import { WithTestID } from "apps/website/types";

export interface ISectionSize {
  top: Size;
  bottom: Size;
}

export interface ISection extends WithTestID {
  /** HTML tag to render (section or div) */
  tag?: "section" | "div";
  /** Vertical spacing size or asymmetric spacing configuration */
  size?: Size | ISectionSize;
  /** Theme applied to the section for background styling */
  theme?: Theme;
  /** Additional CSS classes applied to the section */
  className?: string;
  /** Component identifier for debugging and testing */
  component?: string;
  /** Content to be rendered inside the section */
  children: ReactNode;
  /** Overflow behavior for section content */
  overflow?: "default" | "show";
  /** Unique identifier for the section element */
  id?: string;
}

/**
 * `Section` is a layout component for grouping related content with consistent vertical spacing and theming.
 *
 * It adds top and bottom spacing using the `<Spacer />` component, applies a theme-based background via Tailwind,
 * and sets a `data-theme` attribute to enable contextual styling of child components using CSS variables.
 *
 * @param tag - HTML tag to render (`section` or `div`)
 * @param size - Vertical spacing, either a single size or an object with `top` and `bottom` values
 * @param theme - Visual theme for background and CSS variable context
 * @param overflow - Controls content overflow (`default` = `overflow-hidden`, `show` = visible overflow)
 * @param className - Custom class overrides for the root element
 * @param component - Component identifier for devtools/debugging
 * @param id - Optional HTML ID attribute for anchoring or in-page navigation
 * @param data-testid - Test ID used for automated testing
 * @param children - React content to render inside the section
 *
 * @example
 * // Default section with constrained content
 * <Section size="xl" theme="default">
 *   <Container>
 *     <h1>Page Title</h1>
 *     <p>Content goes here...</p>
 *   </Container>
 * </Section>
 *
 * @example
 * // Hero section with large spacing and brand theme
 * <Section size="2xl" theme="brand">
 *   <Container>
 *     <h1 className="text-white">Hero Title</h1>
 *     <p className="text-white/80">Hero content...</p>
 *   </Container>
 * </Section>
 *
 * @example
 * // Section with asymmetric spacing
 * <Section size={{ top: "xl", bottom: "lg" }} theme="light">
 *   <Container>
 *     <h2>Section with different top/bottom spacing</h2>
 *   </Container>
 * </Section>
 *
 * @example
 * // Section without container (full-width content)
 * <Section size="lg" theme="dark">
 *   <div className="px-4">
 *     <h2>Full-width content</h2>
 *   </div>
 * </Section>
 */

const Section = forwardRef<HTMLDivElement, ISection>(({
  tag = "section",
  size = "xl",
  theme = "default",
  component = "Section",
  className = "",
  "data-testid": testId,
  overflow = "default",
  id,
  children,
}, ref) => {
  const Tag = tag;
  const sectionSize = typeof size === "string" ? { top: size, bottom: size } : { top: size.top, bottom: size.bottom };
  const overflowClass = overflow === "default" ? "overflow-hidden" : "";

  return (
    <Tag
      id={id}
      data-testid={testId}
      data-component={component}
      className={`flex flex-col ${className} ${themeRootClassMap[theme]} ${overflowClass}`}
      data-theme={theme}
      ref={ref}
    >
      <Spacer size={sectionSize.top} />
      <div data-children className="h-full">
        { children }
      </div>
      <Spacer size={sectionSize.bottom} />
    </Tag>
  );
});

// Debugging Purposes because of the forwardRef
Section.displayName = "Section";

export default memo(Section);
